import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { Policy, PolicyDetails, PolicyRow, PolicyUnderwriterResponse, PolicyUnderwriterRow } from '../models/Policy';
import { UserContextProvider } from '../../../infrastructure/context/user-context-provider';
import { AdminPortalAPIHttpClient } from '../../../infrastructure/http/v2/AdminPortalAPIHttpClient';
import { formatCurrencyString } from '../../../utilities/CurrencyUtility';
import { isStringEmptyOrNull } from '../../../utilities/SpringUtility';

@Injectable({
  providedIn: 'root'
})
export class PolicyService {

  constructor(
    private userContextProvider: UserContextProvider,
    private adminPortalHttpClient: AdminPortalAPIHttpClient,
    private snackbar: MatSnackBar
  ) { }

  async getPolicies(): Promise<Policy[]> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.get(`/policies/query`));
      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while retrieving policies: ${JSON.stringify(e)}`);
    }
  }

  async getPropertyPolicies(): Promise<Policy[]> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.get(`/policies/properties`));

      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while retrieving Property Policies`);
    }
  }

  async getPolicyDetail(policyId: string): Promise<PolicyDetails> {
    const response: any = await firstValueFrom(this.adminPortalHttpClient.get(`/policies/${policyId}`));
    return Promise.resolve(response?.data);
  }

  async createPolicy(payload: any): Promise<any> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.post(`/policies`, payload));
      if (!response) {
        return Promise.reject(`Create policy response null`);
      } else {
        if (response.err != null && !isStringEmptyOrNull(response.err.msg)) {
          this.displaySnackbar(response.err.msg);
        } else {
          this.displaySnackbar(response.err);
        }
      }

      return response.data;
    } catch (e) {
      return Promise.reject(`Error while creating policy: ${e}`);
    }
  }

  async updatePolicy(policyId: number, policy: Policy): Promise<Policy> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.put(`/policies/${policyId}`, policy));
      if (!response) {
        return Promise.reject(`Update insurance policy response null ${policyId}`);
      } else {
        if (response.err != null && !isStringEmptyOrNull(response.err.msg)) {
          this.displaySnackbar(response.err.msg);
        } else {
          this.displaySnackbar(response.err);
        }
      }

      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while updating insurance policy ${policyId}: ${e}`);
    }
  }

  async updatePolicyStatus(policyId: number, policy: any): Promise<Policy> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.put(`/policies/${policyId}`, policy));
      if (!response) {
        return Promise.reject(`Update policy status response null ${policyId}`);
      } else {
        if (response.err != null && !isStringEmptyOrNull(response.err.msg)) {
          this.displaySnackbar(response.err.msg);
        } else {
          this.displaySnackbar(response.err);
        }
      }

      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while updating policy status ${policyId}: ${e}`);
    }
  }

  async createPolicyByComponent(payload: any, component: string): Promise<any> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.post(`/policies/${component}`, payload));
      if (!response) {
        return Promise.reject(`Create ${component} policy response null`);
      } else {
        if (response.err != null && !isStringEmptyOrNull(response.err.msg)) {
          this.displaySnackbar(response.err.msg);
        } else {
          this.displaySnackbar(response.err);
        }
      }

      return response.data;
    } catch (e) {
      return Promise.reject(`Error while creating ${component} policy: ${e}`);
    }
  }

  async updatePolicyByComponent(policyId: any, policy: any, component: string): Promise<Policy> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.put(`/policies/${component}/${policyId}`, policy));
      if (!response) {
        return Promise.reject(`Update ${component} policy response null ${policyId}`);
      } else {
        if (response.err != null && !isStringEmptyOrNull(response.err.msg)) {
          this.displaySnackbar(response.err.msg);
        } else {
          this.displaySnackbar(response.err);
        }
      }

      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while updating ${component} policy ${policyId}: ${e}`);
    }
  }

  async deletePolicyByComponent(policyId: any, policy: Policy, component: string): Promise<Policy> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.put(`/policies/${component}/${policyId}`, policy));
      if (!response) {
        return Promise.reject(`Delete ${component} policy response null ${policyId}`);
      } else {
        if (response.err != null && !isStringEmptyOrNull(response.err.msg)) {
          this.displaySnackbar(response.err.msg);
        } else {
          this.displaySnackbar(response.err);
        }
      }

      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while deleting ${component} policy ${policyId}: ${e}`);
    }
  }

  async createPolicyCountry(payload: any): Promise<any> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.post(`/policies/countries`, payload));
      if (!response) {
        return Promise.reject(`Create policy country response null`);
      } else {
        if (response.err != null && !isStringEmptyOrNull(response.err.msg)) {
          this.displaySnackbar(response.err.msg);
        } else {
          this.displaySnackbar(response.err);
        }
      }

      return response.data;
    } catch (e) {
      return Promise.reject(`Error while creating policy country: ${e}`);
    }
  }

  async deletePolicyCountry(policyId: number, countryId: number): Promise<any> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.delete(`/policies/countries/delete?policy_id=${policyId}&country_id=${countryId}`));
      if (!response) {
        return Promise.reject(`Delete policy country response null`);
      } else {
        if (response.err != null && !isStringEmptyOrNull(response.err.msg)) {
          this.displaySnackbar(response.err.msg);
        } else {
          this.displaySnackbar(response.err);
        }
      }

      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while deleting policy country: ${e}`);
    }
  }


  displaySnackbar(errorMessage: string) {
    if (isStringEmptyOrNull(errorMessage)) {
      this.displaySuccessSnackbar();
    } else {
      this.displayErrorSnackbar(errorMessage);
    }
  }

  displaySuccessSnackbar() {
    this.snackbar.open('Save Successful', 'OK', { duration: 4000 });
  }

  displayErrorSnackbar(errorMessage: string) {
    this.snackbar.open(`Error Saving Policy: ${errorMessage}`, 'OK', { duration: 4000 });
  }

  getPolicyRowFromPolicy = (policy: Policy): PolicyRow => {
    const priceModel = this.getPriceModelById(policy.policy_price.pricing_model_id);
    const currencyCode = this.userContextProvider.currencyCode;

    return {
      id: policy.id,
      name: policy.policy.name,
      accountId: policy.account_id,
      insurancePolicyId: policy.insurance_policy_id,
      policyPriceId: policy.policy_price_id,
      contentsCoverage: formatCurrencyString(String(policy.policy.damage_coverage), currencyCode),
      hoLiabilityCoverage: formatCurrencyString(String(policy.policy.liability_coverage), currencyCode),
      dwellingCoverage: formatCurrencyString(String(policy.policy.dwelling_coverage), currencyCode),
      deductible: formatCurrencyString(String(policy.policy_price.deductible), currencyCode),
      priceRate: policy.policy_price.name,
      priceModel: priceModel,
      price: priceModel === 'Tiered nights' ? 'View Night Ranges' : formatCurrencyString(String(policy.policy_price.price), currencyCode),
      pillStatus: policy.is_primary ? 'Primary' : '',
    }
  }

  getPriceModelById = (id: number): string => {
    switch(id) {
      case 1:
        return 'Tiered nights';
      case 2:
        return 'Per night';
      case 3:
        return 'Per reservation';
      default:
        return '';
    }
  }

  getPolicyUnderwriterRowFromUndewriter = (underwriter: PolicyUnderwriterResponse): PolicyUnderwriterRow => {
    const pricingModel = this.getPriceModelById(underwriter.policy_price.pricing_model_id);
    const currencyCode = this.userContextProvider.currencyCode;

    return {
      id: underwriter.id,
      name: underwriter.underwriter.name,
      policyPriceId: underwriter.policy_price_id,
      policy_price_name: underwriter.policy_price.name,
      pricing_model: pricingModel,
      price_rate_id: underwriter.policy_price.id,
      underwriter_id: underwriter.underwriter_id,
      price: pricingModel === 'Tiered nights' ? 'View Night Ranges' : formatCurrencyString(String(underwriter.policy_price.price), currencyCode),
      deductible: formatCurrencyString(String(underwriter.policy_price.deductible), currencyCode)
    }
  }
}